import { useParams } from 'react-router-dom';
import {
  Content,
  EmptyState,
  Header,
  Link,
  MarkdownContent,
  Page,
  ResponseErrorPanel,
} from '@backstage/core-components';
import { makeStyles } from '@material-ui/core';
import Skeleton from '@material-ui/lab/Skeleton';
import { useMarketplace, useMarkdownFile, useSourceUrl } from './api';
import type { ItemKind } from './api';

/**
 * A single skill, agent or command, rendered from its markdown.
 *
 * The frontmatter is lifted out and shown as the subtitle rather than dumped
 * into the body — for a skill that field is the trigger text Claude matches
 * on, so it is the most useful line on the page, not preamble.
 */

const useStyles = makeStyles({
  source: { marginBottom: 14, fontSize: '0.8125rem' },
  body: {
    // Long skill bodies read better bounded than full-bleed.
    maxWidth: 860,
  },
});

export const ItemPage = () => {
  const classes = useStyles();
  const {
    pluginName = '',
    itemKind = '',
    itemName = '',
  } = useParams<{ pluginName: string; itemKind: ItemKind; itemName: string }>();

  // The index, not the plugin detail: it already lists every item's path, and
  // it is almost always in hand from the page before. Asking for the detail
  // here put the plugin's whole fan-out in front of the one file this page shows.
  const { data, error: pluginError, loading: pluginLoading } = useMarketplace();

  const contents = data?.plugins.find(p => p.name === pluginName)?.contents;
  const item = contents
    ? [...contents.skills, ...contents.agents, ...contents.commands].find(
        i => i.kind === itemKind && i.name === itemName,
      )
    : undefined;

  const { file, error, loading } = useMarkdownFile(item?.path);
  const sourceUrl = useSourceUrl(item?.path);

  const missing = Boolean(data && !item);

  return (
    <Page themeId="home">
      {/* No type/typeLink: Header renders those as its own inline trail, which
          would repeat the breadcrumbs directly above it. */}
      <Header title={itemName} subtitle={file?.frontmatter.description} />
      <Content>
        {(pluginLoading || (item && loading)) && (
          <div className={classes.body} data-testid="item-skeleton">
            <Skeleton variant="text" width={90} />
            <Skeleton variant="text" width="50%" height={36} />
            {Array.from({ length: 6 }, (_, i) => (
              <Skeleton
                key={i}
                variant="text"
                width={i === 5 ? '60%' : '100%'}
              />
            ))}
          </div>
        )}
        {(error ?? pluginError) && (
          <ResponseErrorPanel error={(error ?? pluginError)!} />
        )}

        {missing && (
          <EmptyState
            missing="content"
            title="Not found"
            description={`${pluginName} has no ${itemKind} named "${itemName}".`}
          />
        )}

        {item && file && (
          <>
            <div className={classes.source}>
              <Link to={sourceUrl}>View source</Link>
            </div>
            <div className={classes.body}>
              <MarkdownContent content={file.body} linkTarget="_blank" />
            </div>
          </>
        )}
      </Content>
    </Page>
  );
};
